import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

interface Option {
  id: string;
  label: string;
  description?: string;
}

interface QuickSelectionPaletteProps {
  title: string;
  subtitle: string;
  options: Option[];
  onSelect: (optionId: string) => void;
  onClose: () => void;
}

export function QuickSelectionPalette({ 
  title, 
  subtitle, 
  options, 
  onSelect, 
  onClose 
}: QuickSelectionPaletteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input/textarea
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      // Escape is owned by the Radix dialog.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(prev => (prev + 1) % options.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(prev => (prev - 1 + options.length) % options.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (options[selectedIndex]) {
          onSelect(options[selectedIndex].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedIndex, options, onSelect]);

  // Mounted only while open (no `open` prop); Radix still owns Escape, the
  // focus trap, and restoring focus to the opener on unmount.
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-black/10"
        onOpenAutoFocus={(e) => {
          // Land on the highlighted option, not the header close button, so
          // Enter selects rather than dismisses.
          e.preventDefault();
          optionsRef.current?.querySelector('button')?.focus();
        }}
        className="top-auto bottom-12 block w-[calc(100%-1rem)] max-w-md translate-y-0 gap-0 overflow-hidden rounded-lg border-border bg-surface-raised p-0 shadow-2xl sm:bottom-32 sm:w-full sm:max-w-md sm:rounded-2xl"
      >
        {/* Header */}
        <div className="px-2 sm:px-4 py-2 sm:py-3 border-b border-border bg-surface-hover/50">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[10px] sm:text-sm font-medium text-foreground truncate">{title}</DialogTitle>
              <DialogDescription className="text-[9px] sm:text-xs text-text-tertiary mt-0.5 truncate">{subtitle}</DialogDescription>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-0.5 sm:p-1.5 hover:bg-surface-hover rounded-md sm:rounded-lg transition-colors shrink-0 ml-1.5 sm:ml-2"
            >
              <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-text-tertiary" />
            </button>
          </div>
        </div>

        {/* Options */}
        <div ref={optionsRef} className="max-h-56 sm:max-h-80 overflow-y-auto">
          {options.map((option, index) => (
            <button
              key={option.id}
              onClick={() => onSelect(option.id)}
              onMouseEnter={() => setSelectedIndex(index)}
              onFocus={() => setSelectedIndex(index)}
              className={`
                w-full text-left px-2 sm:px-4 py-2 sm:py-3 transition-all border-b border-border last:border-0
                ${selectedIndex === index
                  ? 'bg-primary/15'
                  : 'hover:bg-surface-hover/50'
                }
              `}
            >
              <div className="flex items-center justify-between gap-1.5 sm:gap-2">
                <div className="flex-1 min-w-0">
                  <div className={`
                    text-[10px] sm:text-sm font-medium truncate
                    ${selectedIndex === index ? 'text-primary' : 'text-foreground'}
                  `}>
                    {option.label}
                  </div>
                  {option.description && (
                    <div className="text-[9px] sm:text-xs text-text-tertiary mt-0.5 line-clamp-2">
                      {option.description}
                    </div>
                  )}
                </div>
                {selectedIndex === index && (
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-primary shrink-0" />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Footer hint */}
        <div className="px-2 sm:px-4 py-1 sm:py-2 bg-surface-hover/50 border-t border-border">
          <p className="text-[9px] sm:text-xs text-text-tertiary text-center">
            Use ↑↓ to navigate • Enter to select • Esc to close
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Preset configurations for common use cases
export const DRAFT_NOTE_OPTIONS: Option[] = [
  { id: 'SOAP', label: 'SOAP', description: 'Subjective, Objective, Assessment, Plan' },
  { id: 'APSO', label: 'APSO', description: 'Assessment, Plan, Subjective, Objective' },
  { id: 'Brief', label: 'Brief', description: 'Condensed clinical summary' }
];

export const DRAFT_PLAN_OPTIONS: Option[] = [
  { id: 'standard', label: 'Standard care', description: 'Routine treatment plan' },
  { id: 'risk_mitigation', label: 'Risk mitigation', description: 'Focus on reducing identified risks' },
  { id: 'symptom_focused', label: 'Symptom-focused', description: 'Target specific symptoms' },
  { id: 'adherence_focused', label: 'Adherence-focused', description: 'Emphasis on treatment adherence' }
];

export const SIMULATION_PRESETS: Option[] = [
  { id: 'baseline', label: 'Baseline (no change)', description: 'Current trajectory without changes' },
  { id: 'increase_therapy', label: 'Increase therapy frequency', description: 'More frequent sessions' },
  { id: 'sleep_intervention', label: 'Add sleep intervention', description: 'Sleep-focused treatment addition' },
  { id: 'reduced_adherence', label: 'Reduced adherence scenario', description: 'Model poor medication adherence' }
];
